import * as React from 'react'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import { assertExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { cancelPromiseManagers, createPromiseManager } from '../util/PromiseManager'
import { initialPromiseState, type PromiseState } from '../util/PromiseState'
import { createStateLens } from '../util/react'
import { FileGuidePresentation } from './FileGuidePresentation'
import type { GuideExecution, ParentInfo } from './Model'
import { ReportGuidePresentation } from './ReportGuidePresentation'
import { UrlGuidePresentation } from './UrlGuidePresentation'
import { WorkflowGuidePresentation } from './WorkflowGuidePresentation'

interface Props {
    allowImageZoom: boolean
    deps: CommonDeps
    fetchGuideExecution: () => Promise<GuideExecution>
    onBack: () => void
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    renderCachedImage: RenderCachedImage
    renderWorkflowGuideExecution: (guide: Guide.SyncGuide<'workflow'>) => React.ReactElement<unknown>
}

interface State {
    guideExecutionState: PromiseState<GuideExecution>
}

export class GuidePresentation extends React.Component<Props, State> {
    public state: State = {
        guideExecutionState: initialPromiseState,
    }

    private promiseManagers = {
        guideExecution: createPromiseManager(() => this.props.deps, createStateLens(this, 'guideExecutionState')),
    }

    public componentDidMount(): void {
        this.reloadData()
    }

    public componentWillUnmount(): void {
        cancelPromiseManagers(this.promiseManagers)
    }

    public render = () => renderCatch(this.props.deps, this.tryRender.bind(this))

    private tryRender = () => {
        return this.promiseManagers.guideExecution.render((guideExecution) => {
            const commonProps = {
                deps: this.props.deps,
                onClickGuideParentInfo: this.props.onClickGuideParentInfo,
                renderCachedImage: this.props.renderCachedImage,
            }
            const variant = guideExecution.variant

            switch (variant) {
                case 'file':
                    return (
                        <FileGuidePresentation
                            {...commonProps}
                            allowImageZoom={this.props.allowImageZoom}
                            guideExecution={guideExecution}
                            onBack={this.props.onBack}
                            onOpenFile={this.props.onOpenFile}
                            onReload={this.reloadData}
                        />
                    )
                case 'report':
                    return (
                        <ReportGuidePresentation
                            {...commonProps}
                            downloadFile={this.props.onOpenFile}
                            guideExecution={guideExecution}
                        />
                    )
                case 'url':
                    return (
                        <UrlGuidePresentation
                            {...commonProps}
                            guideExecution={guideExecution}
                            onDone={this.props.onBack}
                        />
                    )
                case 'workflow':
                    return (
                        <WorkflowGuidePresentation
                            {...commonProps}
                            guideExecution={guideExecution}
                            renderWorkflowGuideExecution={this.props.renderWorkflowGuideExecution}
                        />
                    )
                default:
                    assertExhaustion(variant, 'Unknown variant')
            }
        })
    }

    private reloadData = () => this.promiseManagers.guideExecution.trigger(this.props.fetchGuideExecution)
}
